import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type SelectionPanelFooter,
  type SelectionPanelSelectAll,
} from '../SelectionPanel/SelectionPanel.types';
import { type SelectionOption } from '../types/selection-option.types';
import { useSelectionAnnouncer } from '../useSelectionAnnouncer/useSelectionAnnouncer';
import {
  type SelectionChangeOutcome,
  type UseSelectionValue,
} from '../useSelectionValue/useSelectionValue';

/**
 * Below this many selectable rows the select-all header is pure chrome — with
 * two options on screen, "select all" is one more click than clicking both.
 */
export const SELECT_ALL_MIN_ROWS = 2;

export interface UseMultiSelectionPanelInput {
  value: readonly string[];
  selection: UseSelectionValue;
  /** The rows currently drawn, in render order. */
  visibleOptions: readonly SelectionOption[];
  /** What the footer counts against. */
  totalCount: number;
  maxSelected?: number;
  hasSelectAll: boolean;
  isReadOnly: boolean;
  /** A filter or the render cap is hiding rows, so select-all names what it sees. */
  isNarrowed: boolean;
  isLoading: boolean;
  isError: boolean;
  /** The total is what has been loaded, not what exists (a server page). */
  isTotalPartial?: boolean;
}

export interface UseMultiSelectionPanel {
  announcement: string;
  setAnnouncement: (text: string) => void;
  announce: (outcome: SelectionChangeOutcome) => void;
  onSelect: (value: string) => void;
  isOptionDisabled: (option: SelectionOption) => boolean;
  selectAll: SelectionPanelSelectAll | undefined;
  footer: SelectionPanelFooter;
  summary: string;
}

/**
 * Everything `MultiSelect` and `ServerMultiSelect` assemble the SAME way.
 *
 * `useSelectionValue` already gives both of them one selection algebra; this
 * gives them one *panel*, which is the other half of the promise. Without it the
 * two hooks held near-identical copies of the announcement mapping, the disabled
 * predicate, the select-all header and the footer — four places for a coded-enum
 * picker and a server-backed one to drift apart again, which is the exact defect
 * this family was rebuilt to end.
 */
export function useMultiSelectionPanel({
  value,
  selection,
  visibleOptions,
  totalCount,
  maxSelected,
  hasSelectAll,
  isReadOnly,
  isNarrowed,
  isLoading,
  isError,
  isTotalPartial,
}: UseMultiSelectionPanelInput): UseMultiSelectionPanel {
  const { t } = useTranslation('uiKit');
  const announcer = useSelectionAnnouncer();
  const { announcement, say: setAnnouncement, announceOutcome: announce } = announcer;

  const onSelect = useCallback(
    (candidate: string) => {
      // The single refusal point: a keyboard, a label click and a programmatic
      // call all pass through here, and it SAYS so. A row that looks live,
      // focuses, clicks and does nothing at all is a worse failure than the
      // `disabled` this replaced.
      if (isReadOnly) {
        announcer.announceReadOnlyRefusal();
        return;
      }

      announce(selection.toggle(candidate));
    },
    [announce, announcer, isReadOnly, selection],
  );

  const isOptionDisabled = useCallback(
    (option: SelectionOption) =>
      option.isDisabled === true || (selection.isAtLimit && !selection.selected.has(option.value)),
    [selection],
  );

  const selectableVisible = useMemo(
    () => visibleOptions.filter((option) => option.isDisabled !== true),
    [visibleOptions],
  );

  const selectAllState = selection.selectAllStateOf(selectableVisible);
  const onSelectAllToggle = useCallback(() => {
    announce(
      selectAllState === 'checked'
        ? selection.unselectAll(selectableVisible)
        : selection.selectAll(selectableVisible),
    );
  }, [announce, selectAllState, selectableVisible, selection]);

  return {
    announcement,
    setAnnouncement,
    announce,
    onSelect,
    isOptionDisabled,
    selectAll:
      hasSelectAll &&
      !isReadOnly &&
      !isLoading &&
      !isError &&
      selectableVisible.length >= SELECT_ALL_MIN_ROWS
        ? {
            state: selectAllState,
            isNarrowed,
            isDisabled: selectAllState !== 'checked' && selection.isAtLimit,
            onToggle: onSelectAllToggle,
          }
        : undefined,
    footer: { selectedCount: value.length, totalCount, isTotalPartial, maxSelected },
    summary: value.length === 0 ? '' : t('multiSelect.summary', { count: value.length }),
  };
}
